import { useMutation, useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  TextAreaField,
  TextInputField,
  SimpleModal,
  useNotifications,
  Flex
} from '@sequencehq/core-components'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/utils/validation'
import { useMemo, useState } from 'react'

interface Props {
  seatMetricId: string
  isOpen?: boolean
  onClose: () => void
  onUpdate?: () => void
}

export const EditSeatMetricDrawer = ({
  seatMetricId,
  isOpen = true,
  onClose,
  onUpdate
}: Props) => {
  const { displayNotification } = useNotifications()
  const [showValidationErrors, setShowValidationErrors] = useState(false)

  const seatMetricQuery = useQuery(dashboard20240730Client.getSeatMetric, {
    id: seatMetricId
  })

  const { mutateAsync: updateSeatMetric, isPending } = useMutation(
    dashboard20240730Client.putSeatMetric,
    {
      onSuccess: () => {
        displayNotification('Seat metric updated', {
          type: 'success'
        })
        onUpdate?.()
      },
      onError: () => {
        displayNotification('Failed to update seat metric', {
          type: 'error'
        })
      }
    }
  )

  const initialValue = useMemo(() => {
    const metric = seatMetricQuery.data

    if (!metric) {
      return {
        label: '',
        description: ''
      }
    }

    return {
      label: metric.label,
      description: metric.description ?? ''
    }
  }, [seatMetricQuery.data])

  const form = useForm({
    value: initialValue,
    fieldConfiguration: [
      { property: 'label', validation: [required] },
      { property: 'description' }
    ],
    showValidationErrors
  })

  const handleSubmit = async () => {
    if (!form.queries.isValid) {
      setShowValidationErrors(true)
      return false
    }

    try {
      const result = await updateSeatMetric({
        id: seatMetricId,
        body: {
          label: form.queries.formData.label,
          description: form.queries.formData.description
        }
      })

      return !!result
    } catch {
      return false
    }
  }

  return (
    <SimpleModal
      data-testid="seatMetric.editDrawer"
      isOpen={isOpen}
      onClose={onClose}
      variant="drawer"
      title="Edit seat metric"
      cancelButtonText="Cancel"
      submitButtonText="Update seat metric"
      submittingButtonText="Updating seat metric..."
      submitDisabled={isPending}
      onSubmit={handleSubmit}
      isLoading={seatMetricQuery.isPending}
    >
      {seatMetricQuery.data && (
        <Flex flexDirection="column" gap="16px">
          <TextInputField
            data-testid="seatMetric.edit.name"
            label="Name"
            placeholder="Responder"
            value={form.fields.label.value}
            onChange={e => form.fields.label.onChange(e.target.value)}
            validationErrors={form.fields.label.validationErrors}
          />
          <TextAreaField
            data-testid="seatMetric.edit.description"
            label="Description"
            placeholder="Track usage for support seats"
            value={form.fields.description.value}
            onChange={form.fields.description.onChange}
            validationErrors={form.fields.description.validationErrors}
          />
        </Flex>
      )}
    </SimpleModal>
  )
}
